import React, { useCallback, useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import { Alert, Badge, Button, Form, Modal, Spinner } from 'react-bootstrap';
import { apiGet, apiPost } from '../../services/api';

function currencySymbol(code) {
  if (code === 'USD') return '$';
  if (code === 'EUR') return '€';
  if (code === 'GBP') return '£';
  return `${code} `;
}

function formatSalaryDetail(min, max, currency, period) {
  const sym = currencySymbol(currency);
  const nf = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });
  const a = nf.format(Number(min));
  const b = nf.format(Number(max));
  const periodPart =
    {
      annual: '/ year',
      monthly: '/ month',
      hourly: '/ hour',
      yearly: '/ year',
      project: '',
    }[period] || '/ month';
  return `${sym}${a}–${sym}${b} ${periodPart}`.trim();
}

function formatEmploymentTitle(value) {
  if (!value) return '—';
  return String(value)
    .split('_')
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1).toLowerCase() : ''))
    .join(' ');
}

function formatSeniority(value) {
  const labels = {
    entry: 'Entry Level',
    mid: 'Mid-Level',
    senior: 'Senior',
    lead: 'Lead / Principal',
  };
  if (Array.isArray(value) && value.length > 0) {
    return value.map((l) => labels[l] || l).join(' / ');
  }
  if (!value) return '—';
  return String(value);
}

function formatApplicationMethod(method) {
  if (method === 'external') return 'External URL';
  if (method === 'email') return 'Email';
  if (method === 'rwm_flow') return 'RWM Flow';
  return method || '—';
}

function boolBadge(value, trueLabel, falseLabel) {
  return (
    <Badge bg={value ? 'success' : 'secondary'} className="rwm-admin__badge me-1">
      {value ? trueLabel : falseLabel}
    </Badge>
  );
}

export default function AdminJobDetailModal({
  job,
  token,
  onClose,
  onApproved,
  onRejected,
  initialShowReject = false,
  handleAuthError,
  setError,
}) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [actionLoading, setActionLoading] = useState(false);
  const [showReject, setShowReject] = useState(initialShowReject);
  const [rejectReason, setRejectReason] = useState('');
  const [rejectError, setRejectError] = useState('');

  const loadDetail = useCallback(async () => {
    if (!token || !job?.id) return;
    setLoading(true);
    setFetchError('');
    try {
      const res = await apiGet(`/api/v1/admin/jobs/${job.id}/detail`, token);
      if (handleAuthError(res)) return;
      if (res.status === 404) {
        setFetchError('Job not found.');
        return;
      }
      if (!res.ok) {
        setFetchError('Could not load job details.');
        return;
      }
      const json = await res.json();
      setDetail(json.data ?? null);
    } catch {
      setFetchError('Could not load job details.');
    } finally {
      setLoading(false);
    }
  }, [job?.id, token, handleAuthError]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  useEffect(() => {
    setShowReject(initialShowReject);
    setRejectReason('');
    setRejectError('');
  }, [job?.id, initialShowReject]);

  const handleApprove = async () => {
    if (!job?.id) return;
    setActionLoading(true);
    setError('');
    try {
      const res = await apiPost(`/api/v1/admin/jobs/${job.id}/approve`, {}, token);
      if (handleAuthError(res)) return;
      if (!res.ok) {
        setError('Could not approve job.');
        return;
      }
      onApproved();
    } catch {
      setError('Could not approve job.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConfirmReject = async () => {
    const trimmed = rejectReason.trim();
    if (trimmed.length < 10) {
      setRejectError('Rejection reason must be at least 10 characters.');
      return;
    }
    if (!job?.id) return;
    setActionLoading(true);
    setRejectError('');
    try {
      const res = await apiPost(`/api/v1/admin/jobs/${job.id}/reject`, { reason: trimmed }, token);
      if (handleAuthError(res)) return;
      if (res.status === 422) {
        const body = await res.json();
        const msg = body?.errors?.reason?.[0] || 'Invalid rejection reason.';
        setRejectError(msg);
        return;
      }
      if (!res.ok) {
        setRejectError('Could not reject job.');
        return;
      }
      onRejected();
    } catch {
      setRejectError('Could not reject job.');
    } finally {
      setActionLoading(false);
    }
  };

  const locationLabel =
    detail?.location_country || detail?.location_eligibility || '—';

  const sanitizedDescription = detail?.description
    ? DOMPurify.sanitize(detail.description)
    : '';

  const rejectConfirmDisabled = rejectReason.trim().length < 10 || actionLoading;

  return (
    <Modal
      show
      onHide={onClose}
      size="lg"
      centered
      data-testid="rwm-admin-job-modal"
    >
      <Modal.Header closeButton>
        <Modal.Title>Pending job review</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {loading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : null}
        {!loading && fetchError ? (
          <Alert variant="danger" className="mb-0">
            {fetchError}
          </Alert>
        ) : null}
        {!loading && detail ? (
          <>
            <section className="mb-4">
              <h5 className="mb-2" data-testid="rwm-admin-job-modal-title">
                {detail.title}
              </h5>
              <p className="text-muted mb-2">
                {detail.category?.name || '—'}
                {' · '}
                {formatEmploymentTitle(detail.employment_type)}
                {' · '}
                {formatSeniority(detail.seniority_level)}
              </p>
              <span
                className="badge rounded-pill rwm-salary-badge px-3 py-2"
                data-testid="rwm-admin-job-modal-salary"
              >
                {formatSalaryDetail(
                  detail.salary_min,
                  detail.salary_max,
                  detail.salary_currency,
                  detail.salary_period,
                )}
              </span>
              <p className="mt-3 mb-1">
                <strong>Location:</strong> {locationLabel}
              </p>
              <p className="mb-1">
                <strong>Application:</strong> {formatApplicationMethod(detail.application_method)}
              </p>
              {detail.application_method === 'external' && detail.application_url ? (
                <p className="mb-0 small">
                  <a href={detail.application_url} target="_blank" rel="noopener noreferrer">
                    {detail.application_url}
                  </a>
                </p>
              ) : null}
              {detail.application_method === 'email' && detail.application_email ? (
                <p className="mb-0 small">
                  <a href={`mailto:${detail.application_email}`}>{detail.application_email}</a>
                </p>
              ) : null}
            </section>

            <section className="mb-4">
              <h6>Description</h6>
              <div
                className="rwm-admin__modal-description border rounded p-3"
                data-testid="rwm-admin-job-modal-description"
                dangerouslySetInnerHTML={{ __html: sanitizedDescription }}
              />
            </section>

            {detail.company ? (
              <section data-testid="rwm-admin-job-modal-company">
                <h6>Company</h6>
                <p className="mb-1 fw-semibold">{detail.company.name}</p>
                <p className="mb-1 text-muted">{detail.company.country || '—'}</p>
                {detail.company.website ? (
                  <p className="mb-2">
                    <a
                      href={detail.company.website}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {detail.company.website}
                    </a>
                  </p>
                ) : null}
                <div className="mb-2">
                  {boolBadge(detail.company.is_approved, 'Approved', 'Not approved')}
                  {boolBadge(detail.company.is_active, 'Active', 'Inactive')}
                </div>
                <p className="mb-0 small text-muted">
                  Jobs submitted: {detail.company.jobs_total} total ·{' '}
                  {detail.company.jobs_pending} pending · {detail.company.jobs_approved} approved
                </p>
              </section>
            ) : null}
          </>
        ) : null}
      </Modal.Body>

      {!loading && detail && !fetchError ? (
        <>
          {showReject ? (
            <div className="px-3 pb-2 border-top pt-3">
              <Form.Group>
                <Form.Label htmlFor="rwm-admin-job-modal-reject-reason">
                  Rejection reason *
                </Form.Label>
                <Form.Control
                  as="textarea"
                  rows={3}
                  id="rwm-admin-job-modal-reject-reason"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  data-testid="rwm-admin-job-modal-reject-reason"
                />
              </Form.Group>
              {rejectError ? (
                <Alert variant="danger" className="mt-2 mb-0 py-2">
                  {rejectError}
                </Alert>
              ) : null}
              <div className="mt-2">
                <Button
                  variant="danger"
                  size="sm"
                  disabled={rejectConfirmDisabled}
                  onClick={handleConfirmReject}
                  data-testid="rwm-admin-job-modal-reject-confirm"
                >
                  Confirm Reject
                </Button>
                <Button
                  variant="link"
                  size="sm"
                  className="ms-2"
                  onClick={() => {
                    setShowReject(false);
                    setRejectReason('');
                    setRejectError('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </div>
          ) : null}

          <Modal.Footer className="d-flex flex-wrap">
            <Button
              variant="outline-secondary"
              onClick={onClose}
              data-testid="rwm-admin-job-modal-back"
            >
              Back to list
            </Button>
            <div className="flex-grow-1" />
            {!showReject ? (
              <>
                <Button
                  variant="outline-danger"
                  disabled={actionLoading}
                  onClick={() => setShowReject(true)}
                  data-testid="rwm-admin-job-modal-reject"
                >
                  Reject
                </Button>
                <Button
                  variant="info"
                  disabled={actionLoading}
                  onClick={handleApprove}
                  data-testid="rwm-admin-job-modal-approve"
                >
                  Approve
                </Button>
              </>
            ) : null}
          </Modal.Footer>
        </>
      ) : (
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            onClick={onClose}
            data-testid="rwm-admin-job-modal-back"
          >
            Back to list
          </Button>
        </Modal.Footer>
      )}
    </Modal>
  );
}
